/**
 * Navigation header bar with back/close, user info popup, and feature toggles.
 *
 * `<NavigationHeader>` reads actions from `NavigationHeaderActionContext`. The
 *  context is populated imperatively by rendering `<NavigationHeaderAction>` in
 * a screen.
 */

import { ArrowLeft, Close } from '@carbon/icons-react';
import { useTheme } from '@repo/theme';
import { HorizBox } from 'component/basics';
import { BreadCrumb, type ButtonType, CTAButton } from 'component/button';
import { spacings } from 'component/constants';
import { SiteLogo } from 'component/site-logo';
import { Tooltip } from 'component/tooltip';
import { UserInfo } from 'component/user-info';
import { useNeedsProfileSetup } from 'data/me';
import {
    getHeaderTitle,
    Header,
    type HeaderBackButtonProps,
    type HeaderOptions,
    NavigationContext,
    type NavigationProp,
    type ParamListBase,
    type Route,
    useNavigation,
} from 'expo-router/react-navigation';
import { getIsInSidebar } from 'platform-specific/url';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useDatastore, useStableCallback } from 'util/datastore';
import { closeSidebar } from 'util/navigate';
import type { TLabel } from 'util/stdtypes';
import { t } from 'util/stdtypes';

type NavigationButtonProps = HeaderBackButtonProps & {
    canGoBack?: boolean | undefined;
};

function NavigationButton({ canGoBack, onPress }: NavigationButtonProps) {
    const { colors } = useTheme();
    const datastore = useDatastore();
    const needsOnboarding = useNeedsProfileSetup();

    function onClose() {
        closeSidebar(datastore);
    }

    if (canGoBack && !needsOnboarding) {
        return (
            <Tooltip alignment={0} position='bottom' tooltipLabel={t('navigation_header.back_button', 'Back')}>
                <BreadCrumb
                    ariaLabel={t('navigation_header.back_button', 'Back')}
                    icon={ArrowLeft}
                    iconProps={{ size: 32, color: colors.colorTextPrimary }}
                    onPress={onPress}
                    testID='back'
                />
            </Tooltip>
        );
    }

    if (getIsInSidebar()) {
        return (
            <Tooltip alignment={0} position='bottom' tooltipLabel={t('modal.close_button', 'Close')}>
                <BreadCrumb
                    ariaLabel={t('modal.close_button', 'Close')}
                    icon={Close}
                    iconProps={{ size: 32, color: colors.colorTextPrimary }}
                    onPress={onClose}
                    testID='close'
                />
            </Tooltip>
        );
    }

    return null;
}

type NavigationHeaderProps = {
    back?: {
        title: string | undefined;
        href: string | undefined;
    };
    isInSidebar?: boolean | undefined;
    large?: boolean | undefined;
    options: HeaderOptions & {
        headerBackTitle?: string;
        title?: string;
    };
    route: Route<string>;
};

export function NavigationHeader({ back, isInSidebar, large, options, route }: NavigationHeaderProps) {
    const paddingHorizontal = large && !isInSidebar ? spacings.xl4 : spacings.lg;

    const { headerBackTitle, headerLeft, headerRight, headerTitle, ...rest } = options;

    /* eslint-disable react/no-unstable-nested-components */
    return (
        <Header
            {...rest}
            back={back}
            headerLeft={
                headerLeft
                    ? ({ label, ...rest }) =>
                          headerLeft({
                              ...rest,
                              label: headerBackTitle ?? label,
                          })
                    : isInSidebar
                      ? (props) => <NavigationButton {...props} />
                      : () => <SiteLogo large={large} />
            }
            headerLeftContainerStyle={{
                paddingLeft: paddingHorizontal,
            }}
            headerRight={headerRight ?? (() => <UserInfo />)}
            headerRightContainerStyle={{
                paddingRight: paddingHorizontal,
            }}
            headerTitle={headerTitle ?? (() => null)}
            headerTitleContainerStyle={{
                marginLeft: 0,
            }}
            title={getHeaderTitle(options, route.name)}
        />
    );
    /* eslint-enable react/no-unstable-nested-components */
}

type NavigationHeaderAction = {
    label: TLabel;
    type?: ButtonType;
    icon?: React.ReactNode;
    disabled?: boolean;
    onPress: () => void;
};

type NavigationHeaderActionContextValue = {
    setPrimaryAction: (action: NavigationHeaderAction | null) => void;
    setSecondaryAction: (action: NavigationHeaderAction | null) => void;
};

const NavigationHeaderActionContext = React.createContext<NavigationHeaderActionContextValue>({
    setPrimaryAction() {},
    setSecondaryAction() {},
});

export function NavigationHeaderActionProvider({ children }: { children: React.ReactNode }) {
    const [baseOptions, setBaseOptions] = useState({});
    const [primaryAction, setPrimaryAction] = useState<NavigationHeaderAction | null>(null);
    const [secondaryAction, setSecondaryAction] = useState<NavigationHeaderAction | null>(null);
    const navigation = useNavigation<NavigationProp<ParamListBase>>();

    const headerActionOptions: HeaderOptions = useMemo(() => {
        if (!primaryAction) {
            return {};
        }

        return {
            // eslint-disable-next-line react/no-unstable-nested-components
            headerRight: () => (
                <HorizBox gap={12}>
                    {secondaryAction && (
                        <CTAButton
                            disabled={secondaryAction.disabled}
                            icon={secondaryAction.icon}
                            label={secondaryAction.label}
                            onPress={secondaryAction.onPress}
                            size='small'
                            type={secondaryAction.type}
                        />
                    )}
                    <CTAButton disabled={primaryAction.disabled} label={primaryAction.label} onPress={primaryAction.onPress} size='small' />
                </HorizBox>
            ),
        };
    }, [primaryAction, secondaryAction]);

    useEffect(() => {
        navigation.setOptions({ ...baseOptions, ...headerActionOptions });
    }, [baseOptions, headerActionOptions, navigation]);

    const navigationProxy = useMemo(() => {
        return new Proxy(navigation, {
            get(target, property, receiver) {
                if (property === 'setOptions') {
                    return setBaseOptions;
                }

                return Reflect.get(target, property, receiver);
            },
        });
    }, [navigation]);

    const value = useMemo(
        () => ({
            setPrimaryAction,
            setSecondaryAction,
        }),
        [setPrimaryAction, setSecondaryAction],
    );

    return (
        <NavigationContext value={navigationProxy}>
            <NavigationHeaderActionContext.Provider value={value}>{children}</NavigationHeaderActionContext.Provider>
        </NavigationContext>
    );
}

type NavigationHeaderActionProps = NavigationHeaderAction & {
    secondary?: boolean;
};

// We need to use useStableCallback because otherwise
// we either update setSessionData every time the callback changes, which
// risks creating dependency loops, or we don't update the button when
// the callback changes, and the button doesn't work.
export function NavigationHeaderAction({
    label,
    secondary = false,
    type = 'primary',
    icon,
    disabled = false,
    onPress,
}: NavigationHeaderActionProps) {
    const context = useContext(NavigationHeaderActionContext);
    const stableOnPress = useStableCallback(onPress);
    const setAction = secondary ? context.setSecondaryAction : context.setPrimaryAction;
    useEffect(() => {
        setAction({ label, disabled, type, icon, onPress: stableOnPress });
        return () => {
            setAction(null);
        };
    }, [label, disabled, type, icon, stableOnPress, setAction]);
    return null;
}
